import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSmartMutation, useSmartQuery } from "@shared/api/queries/hooks";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { toast } from "@shared/hooks/use-toast";
import {
  Certification,
  CertificationAPI,
  CreateReadingRequest,
  Rate,
  RateAPI,
  Role,
  RoleAPI,
  Skill,
  SkillAPI,
  SlaRule,
  SlaRuleAPI,
  Task,
  TaxRate,
  TaxRateAPI,
  UpdateReadingRequest,
  WorkOrderType,
  WorkOrderTypeAPI,
} from "./types";
import {
  certificationsApi,
  materialsApi,
  ppeEquipmentApi,
  ratesApi,
  readingsApi,
  rolesApi,
  safetyPackageApi,
  safetyProtocolApi,
  skillsApi,
  slaRulesApi,
  tasksApi,
  taxRatesApi,
  workOrderSubTypesApi,
  workOrderTypesApi,
} from "./api";
import {
  transformMaterial,
  transformPPEEquipment,
  transformReading,
  transformSafetyPackage,
  transformSafetyPackageWithLookup,
  transformSafetyProtocol,
  transformTask,
} from "./utils";
import { convertToSnakeCase } from "@shared/utils/caseConverter";
const transformCertification = (apiCert: CertificationAPI): Certification => {
  const createdDate = new Date(apiCert.createdDate);
  const isValidDate = !isNaN(createdDate.getTime());

  return {
    id: apiCert.code,
    name: apiCert.name,
    description: apiCert.description, // Not provided by API
    createdBy: apiCert.createdBy,
    createdDate: apiCert.createdDate,
    isActive: apiCert.isActive,
    code: apiCert.code,
  };
};
// Transform API data to component format
const transformWorkOrderType = (apiType: WorkOrderTypeAPI): WorkOrderType => {
  const createdDate = new Date(apiType.createdDate);
  const isValidDate = !isNaN(createdDate.getTime());

  return {
    id: apiType.code,
    name: apiType.name,
    description: apiType.description, // API doesn't have description field
    createdBy: apiType.createdBy,
    createdDate: apiType.createdDate,
    isActive: apiType.isActive,
    code: apiType.code,
    hierarchyNames: {
      sopType: apiType.hierarchyNames?.sopType,
      sopSubtype: apiType.hierarchyNames?.sopSubtype,
    },
  };
};
const transformRate = (apiRate: RateAPI): Rate => {
  // Generate name based on rate_type
  const name = apiRate.rateType === 0 ? "Normal Rate" : "Overtime Rate";

  return {
    id: apiRate.id,
    code: apiRate.code,
    name: name,
    rate: `${apiRate.rate}`,
    type: apiRate.rateType === 0 ? "normal" : "overtime",
    createdBy: apiRate.createdUserRemoteName,
    description: apiRate.description,
    createdDate: apiRate.createdDate,
    isActive: apiRate.isActive,
  };
};
const transformSlaRule = (apiRule: SlaRuleAPI): SlaRule => {
  const createdDate = new Date(apiRule.createdDate);
  const isValidDate = !isNaN(createdDate.getTime());

  return {
    id: apiRule.id,
    name: apiRule.name,
    description: apiRule.description,
    responseTime: undefined, // Not provided by API - you can add this field later
    createdBy: apiRule.createdUserRemoteName,
    createdDate: apiRule.createdDate,
    isActive: apiRule.isActive,
    remoteUtilityId: apiRule.remoteUtilityId,
  };
};
const transformTaxRate = (apiRate: TaxRateAPI): TaxRate => {
  const createdDate = new Date(apiRate.createdDate);
  const isValidDate = !isNaN(createdDate.getTime());

  // Generate a display name based on the rate value
  const rateValue = apiRate.name;
  const displayName = `${rateValue}`;

  return {
    id: apiRate.code,
    name: displayName,
    rate: `${rateValue}%`,
    description: apiRate.description, // Not provided by API
    createdBy: apiRate.createdBy,
    createdDate: apiRate.createdDate,
    isActive: apiRate.isActive,
    code: apiRate.code,
  };
};
export const useWorkOrderTypes = (searchQuery?: string) => {
  const utilityId = useRemoteUtilityId();

  return useSmartQuery(
    [QueryKeyFactory.module.wx.settings.types(), searchQuery],
    async () => {
      const response = await workOrderTypesApi.getWorkOrderTypes(
        utilityId,
        searchQuery
      );

      return response.result.map(transformWorkOrderType);
    },
    {
      enabled: !!utilityId,
      staleTime: 5 * 60 * 1000,
    }
  );
};

export const useCreateWorkOrderType = () => {
  const utilityId = useRemoteUtilityId();
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (data: { name: string; description: string }) => {
      if (!utilityId) throw new Error("Utility ID is missing");

      // Generate code from name (you might want to adjust this logic)
      const code = data.name.replace(/\s+/g, " ").trim();

      return workOrderTypesApi.createWorkOrderType({
        name: data.name,
        remoteUtilityId: utilityId,
        code,
        description: data.description,
      });
    },
    {
      onSuccess: () => {
        queryClient.refetchQueries({
          queryKey: [QueryKeyFactory.module.wx.settings.types()],
        });
        toast({
          title: "Success",
          description: "Work order type created successfully",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to create work order type",
          variant: "destructive",
        });
      },
    }
  );
};

export const useUpdateWorkOrderType = () => {
  const utilityId = useRemoteUtilityId();
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (data: { code: string; name: string; description }) => {
      if (!utilityId) throw new Error("Utility ID is missing");

      return workOrderTypesApi.updateWorkOrderType({
        name: data.name,
        remoteUtilityId: utilityId,
        code: data.code,
        description: data.description,
      });
    },
    {
      onSuccess: () => {
        queryClient.refetchQueries({
          queryKey: [QueryKeyFactory.module.wx.settings.types()],
        });
        toast({
          title: "Success",
          description: "Work order type updated successfully",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to update work order type",
          variant: "destructive",
        });
      },
    }
  );
};

export const useToggleWorkOrderTypeStatus = () => {
  const utilityId = useRemoteUtilityId();
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (data: { code: string; isActive: boolean; name: string }) => {
      if (!utilityId) throw new Error("Utility ID is missing");

      return workOrderTypesApi.toggleWorkOrderTypeStatus(
        data.code,
        data.isActive,
        utilityId
      );
    },
    {
      onSuccess: (_, variables) => {
        queryClient.refetchQueries({
          queryKey: [QueryKeyFactory.module.wx.settings.types()],
        });
        toast({
          title: "Status Changed",
          description: `${variables.name} is now ${
            variables.isActive ? "isActive" : "inactive"
          }`,
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to update status",
          variant: "destructive",
        });
      },
    }
  );
};
export const useWorkOrderSubTypes = (searchQuery?: string) => {
  const utilityId = useRemoteUtilityId();

  return useSmartQuery(
    [QueryKeyFactory.module.wx.settings.subTypes(), searchQuery],
    async () => {
      const response = await workOrderSubTypesApi.getWorkOrderSubTypes(
        utilityId,
        searchQuery
      );
      return response.result.map(transformWorkOrderType);
    },
    {
      enabled: !!utilityId,
      staleTime: 5 * 60 * 1000,
    }
  );
};

export const useCreateWorkOrderSubType = () => {
  const utilityId = useRemoteUtilityId();
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (data: { name: string; code: string; description?: string }) => {
      if (!utilityId) throw new Error("Utility ID is missing");

      // Generate code following the pattern: parentCode_subTypeName
      const code = `${data.code}_${data.name.replace(/\s+/g, " ").trim()}`;

      return workOrderSubTypesApi.createWorkOrderSubType({
        name: data.name,
        remoteUtilityId: utilityId,
        code: code,
        description: data.description,
      });
    },
    {
      onSuccess: () => {
        queryClient.refetchQueries({
          queryKey: [QueryKeyFactory.module.wx.settings.subTypes()],
        });
        toast({
          title: "Success",
          description: "Work order sub-type created successfully",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to create work order sub-type",
          variant: "destructive",
        });
      },
    }
  );
};

export const useUpdateWorkOrderSubType = () => {
  const utilityId = useRemoteUtilityId();
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (data: { code: string; name: string; description?: string }) => {
      if (!utilityId) throw new Error("Utility ID is missing");

      return workOrderSubTypesApi.updateWorkOrderSubType({
        name: data.name,
        remoteUtilityId: utilityId,
        code: data.code,
        description: data.description,
      });
    },
    {
      onSuccess: () => {
        queryClient.refetchQueries({
          queryKey: [QueryKeyFactory.module.wx.settings.subTypes()],
        });
        toast({
          title: "Success",
          description: "Work order sub-type updated successfully",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to update work order sub-type",
          variant: "destructive",
        });
      },
    }
  );
};

export const useToggleWorkOrderSubTypeStatus = () => {
  const utilityId = useRemoteUtilityId();
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (data: { code: string; isActive: boolean; name: string }) => {
      if (!utilityId) throw new Error("Utility ID is missing");

      return workOrderSubTypesApi.toggleWorkOrderSubTypeStatus(
        data.code,
        data.isActive,
        utilityId
      );
    },
    {
      onSuccess: (_, variables) => {
        queryClient.refetchQueries({
          queryKey: [QueryKeyFactory.module.wx.settings.subTypes()],
        });
        toast({
          title: "Status Changed",
          description: `${variables.name} is now ${
            variables.isActive ? "isActive" : "inactive"
          }`,
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to update status",
          variant: "destructive",
        });
      },
    }
  );
};
export const useSlaRules = (searchQuery?: string) => {
  const utilityId = useRemoteUtilityId();

  return useSmartQuery(
    [QueryKeyFactory.module.wx.settings.slaRules(), searchQuery],
    async () => {
      const response = await slaRulesApi.getSlaRules(utilityId, searchQuery);
      return response.result.map(transformSlaRule);
    }
  );
};

export const useCreateSlaRule = () => {
  const utilityId = useRemoteUtilityId();
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (data: { name: string; description: string }) => {
      if (!utilityId) throw new Error("Utility ID is missing");

      return slaRulesApi.createSlaRule({
        name: data.name,
        description: data.description,
        remoteUtilityId: utilityId,
      });
    },
    {
      onSuccess: () => {
        queryClient.refetchQueries({
          queryKey: [QueryKeyFactory.module.wx.settings.slaRules()],
        });
        toast({
          title: "Success",
          description: "SLA rule created successfully",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to create SLA rule",
          variant: "destructive",
        });
      },
    }
  );
};

export const useUpdateSlaRule = () => {
  const utilityId = useRemoteUtilityId();
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (data: { id: number; name: string; description: string }) => {
      if (!utilityId) throw new Error("Utility ID is missing");

      return slaRulesApi.updateSlaRule(data.id, {
        name: data.name,
        description: data.description,
        remoteUtilityId: utilityId,
      });
    },
    {
      onSuccess: () => {
        queryClient.refetchQueries({
          queryKey: [QueryKeyFactory.module.wx.settings.slaRules()],
          exact: false,
        });
        toast({
          title: "Success",
          description: "SLA rule updated successfully",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to update SLA rule",
          variant: "destructive",
        });
      },
    }
  );
};

export const useToggleSlaRuleStatus = () => {
  const utilityId = useRemoteUtilityId();
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (data: { id: number; isActive: boolean; name: string }) => {
      if (!utilityId) throw new Error("Utility ID is missing");

      return slaRulesApi.toggleSlaRuleStatus(data.id, {
        remoteUtilityId: utilityId,
        isActive: data.isActive,
      });
    },
    {
      onSuccess: (_, variables) => {
        queryClient.refetchQueries({
          queryKey: [QueryKeyFactory.module.wx.settings.slaRules()],
        });
        toast({
          title: "Status Changed",
          description: `${variables.name} is now ${
            variables.isActive ? "isActive" : "inactive"
          }`,
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to update status",
          variant: "destructive",
        });
      },
    }
  );
};
export const useTaxRates = () => {
  const utilityId = useRemoteUtilityId();

  return useSmartQuery(
    [QueryKeyFactory.module.wx.settings.taxRates()],
    async () => {
      const response = await taxRatesApi.getTaxRates(utilityId);
      return response.result.map(transformTaxRate);
    },
    {
      enabled: !!utilityId,
      staleTime: 5 * 60 * 1000,
    }
  );
};

export const useTaxRatesSearch = (searchQuery?: string) => {
  const utilityId = useRemoteUtilityId();

  return useSmartQuery(
    [QueryKeyFactory.module.wx.settings.taxRates(), searchQuery],
    async () => {
      const response = await taxRatesApi.taxRatesSearch(utilityId, searchQuery);
      return response.result.map(transformTaxRate);
    },
    {
      enabled: !!utilityId && !!searchQuery,
      staleTime: 5 * 60 * 1000,
    }
  );
};

export const useCreateTaxRate = () => {
  const utilityId = useRemoteUtilityId();
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (data: { name: string; rate: string; description: string }) => {
      if (!utilityId) throw new Error("Utility ID is missing");

      // Extract numeric value from rate (remove % if present)
      const rateValue = data?.name;

      // Use the rate value as both name and code for creation
      return taxRatesApi.createTaxRate({
        name: rateValue, // API expects the rate value in name field
        remoteUtilityId: utilityId,
        code: rateValue, // Use rate value as base code
        description: data.description,
      });
    },
    {
      onSuccess: () => {
        queryClient.refetchQueries({
          queryKey: [QueryKeyFactory.module.wx.settings.taxRates()],
        });
        toast({
          title: "Success",
          description: "Tax rate created successfully",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to create tax rate",
          variant: "destructive",
        });
      },
    }
  );
};

export const useUpdateTaxRate = () => {
  const utilityId = useRemoteUtilityId();
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (data: {
      code: string;
      name: string;
      rate: string;
      description: string;
    }) => {
      if (!utilityId) throw new Error("Utility ID is missing");

      // Extract numeric value from rate (remove % if present)
      const rateValue = data?.name;

      return taxRatesApi.updateTaxRate({
        name: rateValue, // API expects the rate value in name field
        remoteUtilityId: utilityId,
        code: data.code, // Use the full code for updates
        description: data.description,
      });
    },
    {
      onSuccess: () => {
        queryClient.refetchQueries({
          queryKey: [QueryKeyFactory.module.wx.settings.taxRates()],
        });
        toast({
          title: "Success",
          description: "Tax rate updated successfully",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to update tax rate",
          variant: "destructive",
        });
      },
    }
  );
};

export const useToggleTaxRateStatus = () => {
  const utilityId = useRemoteUtilityId();
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (data: { code: string; isActive: boolean; name: string }) => {
      if (!utilityId) throw new Error("Utility ID is missing");

      return taxRatesApi.toggleTaxRateStatus({
        code: data.code,
        isActive: data.isActive,
        remoteUtilityId: utilityId,
      });
    },
    {
      onSuccess: (_, variables) => {
        queryClient.refetchQueries({
          queryKey: [QueryKeyFactory.module.wx.settings.taxRates()],
        });
        toast({
          title: "Status Changed",
          description: `${variables.name} is now ${
            variables.isActive ? "isActive" : "inactive"
          }`,
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to update status",
          variant: "destructive",
        });
      },
    }
  );
};
export const useCertifications = () => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    [QueryKeyFactory.module.wx.settings.certifications()], // Include search in queryKey
    () => certificationsApi.getCertifications(utilityId),
    {
      enabled: !!utilityId, // Only run query if utilityId is available
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    }
  );
};

export const useCertificationsSearch = (searchQuery?: string) => {
  const utilityId = useRemoteUtilityId();
  return useSmartQuery(
    [QueryKeyFactory.module.wx.settings.certifications(), searchQuery], // Include search in queryKey
    () => certificationsApi.certificationsSearch(utilityId, searchQuery),
    {
      enabled: !!utilityId && !!searchQuery, // Only run query if utilityId is available
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    }
  );
};

export const useCreateCertification = () => {
  const utilityId = useRemoteUtilityId();
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (data: { name: string; description?: string }) => {
      if (!utilityId) throw new Error("Utility ID is missing");

      // Use the name as the base code for creation
      const baseCode = data.name.trim();
      const payload = convertToSnakeCase({
        name: data.name.trim(),
        remoteUtilityId: utilityId,
        code: baseCode,
        description: data.description,
      });

      return certificationsApi.createCertification(payload);
    },
    {
      onSuccess: () => {
        queryClient.refetchQueries({
          queryKey: [QueryKeyFactory.module.wx.settings.certifications()],
        });
        toast({
          title: "Success",
          description: "Certification created successfully",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to create certification",
          variant: "destructive",
        });
      },
    }
  );
};

export const useUpdateCertification = () => {
  const utilityId = useRemoteUtilityId();
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (data: { code: string; name: string; description?: string }) => {
      if (!utilityId) throw new Error("Utility ID is missing");
      const payload = convertToSnakeCase({
        name: data.name.trim(),
        remoteUtilityId: utilityId,
        code: data.code, // Use the full code for updates
        description: data.description,
      });

      return certificationsApi.updateCertification(payload);
    },
    {
      onSuccess: () => {
        queryClient.refetchQueries({
          queryKey: [QueryKeyFactory.module.wx.settings.certifications()],
        });
        toast({
          title: "Success",
          description: "Certification updated successfully",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to update certification",
          variant: "destructive",
        });
      },
    }
  );
};

export const useToggleCertificationStatus = () => {
  const utilityId = useRemoteUtilityId();
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (data: { code: string; isActive: boolean; name: string }) => {
      if (!utilityId) throw new Error("Utility ID is missing");

      const payload = convertToSnakeCase({
        code: data.code,
        isActive: data.isActive,
        remoteUtilityId: utilityId,
      });
      return certificationsApi.toggleCertificationStatus(payload);
    },
    {
      onSuccess: (_, variables) => {
        queryClient.refetchQueries({
          queryKey: [QueryKeyFactory.module.wx.settings.certifications()],
        });
        toast({
          title: "Status Changed",
          description: `${variables.name} is now ${
            variables.isActive ? "isActive" : "inactive"
          }`,
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to update status",
          variant: "destructive",
        });
      },
    }
  );
};
const transformSkill = (apiSkill: SkillAPI): Skill => {
  return {
    id: apiSkill.id,
    name: apiSkill.skill,
    description: apiSkill.description || undefined,
    createdBy: apiSkill.createdUserRemoteName || "Unknown",
    createdDate: apiSkill.createdDate,
    isActive: apiSkill.isActive,
  };
};

export const useSkills = (searchQuery?: string) => {
  const utilityId = useRemoteUtilityId();

  return useSmartQuery(
    // Include search query in cache key for proper caching
    [...QueryKeyFactory.module.wx.settings.skills(), searchQuery],
    async () => {
      const response = await skillsApi.getSkills(utilityId, searchQuery);
      return response.result.map(transformSkill);
    },
    {
      enabled: !!utilityId,
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
    }
  );
};

export const useCreateSkill = () => {
  const utilityId = useRemoteUtilityId();
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (data: { name: string; description?: string }) => {
      if (!utilityId) throw new Error("Utility ID is missing");

      return skillsApi.createSkill({
        skill: data.name,
        description: data.description,
        remoteUtilityId: utilityId,
      });
    },
    {
      onSuccess: () => {
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.wx.settings.skills(),
          exact: false,
        });
        toast({
          title: "Success",
          description: "Skill created successfully",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to create skill",
          variant: "destructive",
        });
      },
    }
  );
};

export const useUpdateSkill = () => {
  const utilityId = useRemoteUtilityId();
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (data: { id: number; name: string; description?: string }) => {
      if (!utilityId) throw new Error("Utility ID is missing");

      return skillsApi.updateSkill(data.id, {
        skill: data.name,
        remoteUtilityId: utilityId,
        code: data.id,
      });
    },
    {
      onSuccess: () => {
        queryClient.refetchQueries({
          queryKey: [QueryKeyFactory.module.wx.settings.skills()],
          exact: false,
        });
        toast({
          title: "Success",
          description: "Skill updated successfully",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to update skill",
          variant: "destructive",
        });
      },
    }
  );
};

export const useToggleSkillStatus = () => {
  const utilityId = useRemoteUtilityId();
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (data: { id: number; isActive: boolean; name: string }) => {
      if (!utilityId) throw new Error("Utility ID is missing");

      return skillsApi.toggleSkillStatus(data.id, {
        remoteUtilityId: utilityId,
        isActive: data.isActive,
      });
    },
    {
      onSuccess: (_, variables) => {
        queryClient.refetchQueries({
          queryKey: QueryKeyFactory.module.wx.settings.skills(),
        });
        toast({
          title: "Status Changed",
          description: `${variables.name} is now ${
            variables.isActive ? "isActive" : "inactive"
          }`,
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to update status",
          variant: "destructive",
        });
      },
    }
  );
};
const transformRole = (apiRole: RoleAPI): Role => {
  const createdDate = new Date(apiRole.createdDate);
  const isValidDate = !isNaN(createdDate.getTime());

  return {
    id: apiRole.id,
    name: apiRole.name,
    description: apiRole.description,
    createdBy: apiRole.createdUserRemoteName,
    createdDate: apiRole.createdDate,
    isActive: apiRole.isActive,
  };
};

export const useRoles = (searchQuery?: string) => {
  const utilityId = useRemoteUtilityId();

  return useSmartQuery(
    [QueryKeyFactory.module.wx.settings.roles(), searchQuery], // Add this to your QueryKeyFactory
    async () => {
      const response = await rolesApi.getRoles(utilityId, searchQuery);
      return response.result.map(transformRole);
    },
    {
      enabled: !!utilityId,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      staleTime: 0,
    }
  );
};

export const useCreateRole = () => {
  const utilityId = useRemoteUtilityId();
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (data: { name: string; description?: string }) => {
      if (!utilityId) throw new Error("Utility ID is missing");

      return rolesApi.createRole({
        name: data.name,
        remoteUtilityId: utilityId,
        description: data.description,
      });
    },
    {
      onSuccess: () => {
        queryClient.refetchQueries({
          queryKey: [QueryKeyFactory.module.wx.settings.roles()],
        });
        toast({
          title: "Success",
          description: "Role created successfully",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to create role",
          variant: "destructive",
        });
      },
    }
  );
};

export const useUpdateRole = () => {
  const utilityId = useRemoteUtilityId();
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (data: { id: number; name: string; description?: string }) => {
      if (!utilityId) throw new Error("Utility ID is missing");

      return rolesApi.updateRole(data.id, {
        name: data.name,
        remoteUtilityId: utilityId,
        code: data.id,
        description: data.description,
      });
    },
    {
      onSuccess: () => {
        queryClient.refetchQueries({
          queryKey: [QueryKeyFactory.module.wx.settings.roles()],
        });
        toast({
          title: "Success",
          description: "Role updated successfully",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to update role",
          variant: "destructive",
        });
      },
    }
  );
};

export const useToggleRoleStatus = () => {
  const utilityId = useRemoteUtilityId();
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (data: { id: number; isActive: boolean; name: string }) => {
      if (!utilityId) throw new Error("Utility ID is missing");

      return rolesApi.toggleRoleStatus(data.id, {
        remoteUtilityId: utilityId,
        isActive: data.isActive,
      });
    },
    {
      onSuccess: (_, variables) => {
        queryClient.refetchQueries({
          queryKey: [QueryKeyFactory.module.wx.settings.roles()],
        });
        toast({
          title: "Status Changed",
          description: `${variables.name} is now ${
            variables.isActive ? "isActive" : "inactive"
          }`,
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to update status",
          variant: "destructive",
        });
      },
    }
  );
};
export const useRates = (searchQuery?: string) => {
  const utilityId = useRemoteUtilityId();

  return useSmartQuery(
    [QueryKeyFactory.module.wx.settings.rates(), searchQuery], // Use dedicated rates query key
    async () => {
      const response = await ratesApi.getRates(utilityId, searchQuery);
      return response.result.map(transformRate);
    },
    {
      enabled: !!utilityId,
    }
  );
};

export const useCreateRate = () => {
  const utilityId = useRemoteUtilityId();
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (data: {
      rate: string;
      type: "normal" | "overtime";
      description: string;
    }) => {
      if (!utilityId) throw new Error("Utility ID is missing");

      return ratesApi.createRate({
        rateType: data.type === "normal" ? "0" : "1",
        rate: data.rate,
        remoteUtilityId: utilityId,
        description: data.description,
      });
    },
    {
      onSuccess: () => {
        queryClient.refetchQueries({
          queryKey: [QueryKeyFactory.module.wx.settings.rates()],
        });
        toast({
          title: "Success",
          description: "Rate created successfully",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to create rate",
          variant: "destructive",
        });
      },
    }
  );
};

export const useUpdateRate = () => {
  const utilityId = useRemoteUtilityId();
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (data: {
      id: number;
      rate: string;
      type: "normal" | "overtime";
      description?: string;
    }) => {
      if (!utilityId) throw new Error("Utility ID is missing");

      return ratesApi.updateRate(data.id, {
        rate_type: data.type === "normal" ? "0" : "1",
        rate: data.rate,
        remoteUtilityId: utilityId,
        description: data.description,
      });
    },
    {
      onSuccess: () => {
        queryClient.refetchQueries({
          queryKey: [QueryKeyFactory.module.wx.settings.rates()],
        });
        toast({
          title: "Success",
          description: "Rate updated successfully",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to update rate",
          variant: "destructive",
        });
      },
    }
  );
};

export const useToggleRateStatus = () => {
  const utilityId = useRemoteUtilityId();
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (data: { id: number; isActive: boolean; name: string }) => {
      if (!utilityId) throw new Error("Utility ID is missing");

      return ratesApi.toggleRateStatus(data.id, {
        remoteUtilityId: utilityId,
        isActive: data.isActive,
      });
    },
    {
      onSuccess: (_, variables) => {
        queryClient.refetchQueries({
          queryKey: [QueryKeyFactory.module.wx.settings.rates()],
        });
        toast({
          title: "Status Changed",
          description: `${variables.name} is now ${
            variables.isActive ? "isActive" : "inactive"
          }`,
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to update status",
          variant: "destructive",
        });
      },
    }
  );
};
export const useReadings = (searchQuery?: string) => {
  const utilityId = useRemoteUtilityId();

  return useSmartQuery(
    [QueryKeyFactory.module.wx.settings.settingReading(), searchQuery],
    async () => {
      if (!utilityId) throw new Error("Utility ID is missing");
      const response = await readingsApi.getReadings(
        parseInt(utilityId),
        searchQuery
      );
      return response.result.map((reading, index) =>
        transformReading(reading, index)
      );
    },
    {
      enabled: !!utilityId,
      staleTime: 5 * 60 * 1000,
    }
  );
};

export const useCreateReading = () => {
  const utilityId = useRemoteUtilityId();
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (data: {
      code: string;
      name: string;
      unit: string;
      default: boolean;
      maximum: string;
      minimum: string;
      photoRequired: boolean;
      operationType: number;
    }) => {
      if (!utilityId) throw new Error("Utility ID is missing");

      const requestData: CreateReadingRequest = {
        remoteUtilityId: utilityId,
        operationType: data.operationType,
        reading: {
          code: data.code,
          name: data.name,
          unit: data.unit,
          default: data.default,
          maximum: data.maximum,
          minimum: data.minimum,
          isPhotoReq: data.photoRequired ? "Yes" : "No",
        },
      };

      return readingsApi.createReading(requestData);
    },
    {
      onSuccess: (result) => {
        queryClient.refetchQueries({
          queryKey: [QueryKeyFactory.module.wx.settings.settingReading()],
        });
        toast({
          title: "Success",
          description: "Reading created successfully",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to create reading",
          variant: "destructive",
        });
      },
    }
  );
};

export const useUpdateReading = () => {
  const utilityId = useRemoteUtilityId();
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (data: {
      code: string;
      key: string;
      name: string;
      unit: string;
      default: boolean;
      maximum: string;
      minimum: string;
      photoRequired: boolean;
      operationType: number;
    }) => {
      if (!utilityId) throw new Error("Utility ID is missing");

      const requestData: UpdateReadingRequest = {
        remoteUtilityId: utilityId,
        operationType: data.operationType,
        reading: {
          code: data.code,
          key: data.key,
          name: data.name,
          unit: data.unit,
          default: data.default,
          maximum: data.maximum,
          minimum: data.minimum,
          isPhotoReq: data.photoRequired ? "Yes" : "No",
        },
      };

      return readingsApi.updateReading(requestData);
    },
    {
      onSuccess: (result) => {
        queryClient.refetchQueries({
          queryKey: [QueryKeyFactory.module.wx.settings.settingReading()],
        });
        toast({
          title: "Success",
          description: "Reading updated successfully",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to update reading",
          variant: "destructive",
        });
      },
    }
  );
};
export const useToggleReadingStatus = () => {
  const utilityId = useRemoteUtilityId();
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (data: {
      code: string;
      isActive: boolean;
      name: string;
      operationType;
    }) => {
      if (!utilityId) throw new Error("Utility ID is missing");

      return readingsApi.toggleReadingStatus({
        code: data.code,
        remoteUtilityId: utilityId,
        isActive: data.isActive,
        operationType: data.operationType,
      });
    },
    {
      onSuccess: (_, variables) => {
        queryClient.refetchQueries({
          queryKey: [QueryKeyFactory.module.wx.settings.settingReading()],
        });
        toast({
          title: "Status Changed",
          description: `${variables.name} is now ${
            variables.isActive ? "active" : "inactive"
          }`,
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to update status",
          variant: "destructive",
        });
      },
    }
  );
};
export const useMaterials = (searchQuery?: string) => {
  const utilityId = useRemoteUtilityId();

  return useSmartQuery(
    [QueryKeyFactory.module.wx.settings.settingMaterial(), searchQuery],
    async () => {
      const response = await materialsApi.getMaterials(utilityId, searchQuery);
      return response.result.map(transformMaterial);
    },
    {
      enabled: !!utilityId,
      staleTime: 5 * 60 * 1000,
    }
  );
};

export const useCreateMaterial = () => {
  const utilityId = useRemoteUtilityId();
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (data: {
      name: string;
      operationType: number;
      size: string;
      quantity: number;
      costPerUnit: number;
    }) => {
      if (!utilityId) throw new Error("Utility ID is missing");

      return materialsApi.createMaterial({
        remoteUtilityId: utilityId,
        operationType: data.operationType || 0,
        material: {
          name: data.name,
          size: data.size,
          quantity: data.quantity,
          costPerUnit: data.costPerUnit,
        },
      });
    },
    {
      onSuccess: () => {
        queryClient.refetchQueries({
          queryKey: [QueryKeyFactory.module.wx.settings.settingMaterial()],
        });
        toast({
          title: "Success",
          description: "Material created successfully",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to create material",
          variant: "destructive",
        });
      },
    }
  );
};

export const useUpdateMaterial = () => {
  const utilityId = useRemoteUtilityId();
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (data: {
      code: string;
      name: string;
      operationType: number;
      size: string;
      quantity: number;
      costPerUnit: number;
    }) => {
      if (!utilityId) throw new Error("Utility ID is missing");

      return materialsApi.updateMaterial({
        remoteUtilityId: utilityId,
        operationType: data.operationType,
        material: {
          code: data.code,
          name: data.name,
          size: data.size,
          quantity: data.quantity,
          costPerUnit: data.costPerUnit,
        },
      });
    },
    {
      onSuccess: () => {
        queryClient.refetchQueries({
          queryKey: [QueryKeyFactory.module.wx.settings.settingMaterial()],
        });
        toast({
          title: "Success",
          description: "Material updated successfully",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to update material",
          variant: "destructive",
        });
      },
    }
  );
};
export const useToggleMaterialStatus = () => {
  const utilityId = useRemoteUtilityId();
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (data: {
      code: string;
      isActive: boolean;
      name: string;
      operationType: number;
    }) => {
      if (!utilityId) throw new Error("Utility ID is missing");

      return materialsApi.toggleMaterialStatus({
        code: data.code,
        remoteUtilityId: utilityId,
        isActive: data.isActive,
        operationType: data.operationType,
      });
    },
    {
      onSuccess: (_, variables) => {
        queryClient.refetchQueries({
          queryKey: [QueryKeyFactory.module.wx.settings.settingMaterial()],
        });
        toast({
          title: "Status Changed",
          description: `${variables.name} is now ${
            variables.isActive ? "active" : "inactive"
          }`,
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to update status",
          variant: "destructive",
        });
      },
    }
  );
};
export const useTasks = (searchQuery?: string) => {
  const utilityId = useRemoteUtilityId();

  return useSmartQuery(
    [QueryKeyFactory.module.wx.settings.settingTask(), searchQuery],
    async () => {
      const response = await tasksApi.getTasks(utilityId, searchQuery);
      return response.result.map(transformTask);
    },
    {
      enabled: !!utilityId,
      staleTime: 5 * 60 * 1000,
    }
  );
};

export const useCreateTask = () => {
  const utilityId = useRemoteUtilityId();
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (data: {
      name: string;
      operationType: number;
      turnaroundTime: number;
      downtime: number;
      skillIds: number[];
      photoRequired: boolean;
    }) => {
      if (!utilityId) throw new Error("Utility ID is missing");

      return tasksApi.createTask({
        remoteUtilityId: utilityId,
        operationType: data.operationType,
        task: {
          name: data.name,
          time: data.turnaroundTime.toString(),
          downtime: data.downtime.toString(),
          skillId: data.skillIds,
          isPhotoReq: data.photoRequired ? "Yes" : "No",
        },
      });
    },
    {
      onSuccess: () => {
        queryClient.refetchQueries({
          queryKey: [QueryKeyFactory.module.wx.settings.settingTask()],
        });
        toast({
          title: "Success",
          description: "Task created successfully",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to create task",
          variant: "destructive",
        });
      },
    }
  );
};

export const useUpdateTask = () => {
  const utilityId = useRemoteUtilityId();
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (data: {
      code: string;
      name: string;
      operationType: number;
      turnaroundTime: number;
      downtime: number;
      skillIds: number[];
      photoRequired: boolean;
      isActive: boolean;
    }) => {
      if (!utilityId) throw new Error("Utility ID is missing");

      return tasksApi.updateTask({
        remoteUtilityId: utilityId,
        operationType: data.operationType,
        task: {
          name: data.name,
          code: data.code,
          time: data.turnaroundTime.toString(),
          downtime: data.downtime.toString(),
          skill_id: data.skillIds,
          isPhotoReq: data.photoRequired ? "Yes" : "No",
          isActive: data.isActive,
        },
      });
    },
    {
      onSuccess: () => {
        queryClient.refetchQueries({
          queryKey: [QueryKeyFactory.module.wx.settings.settingTask()],
        });
        toast({
          title: "Success",
          description: "Task updated successfully",
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to update task",
          variant: "destructive",
        });
      },
    }
  );
};

export const useToggleTaskStatus = () => {
  const utilityId = useRemoteUtilityId();
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (data: {
      code: string;
      isActive: boolean;
      name: string;
      operationType: number;
    }) => {
      if (!utilityId) throw new Error("Utility ID is missing");

      return tasksApi.toggleTaskStatus({
        code: data.code,
        remoteUtilityId: utilityId,
        isActive: data.isActive,
        operationType: +data.operationType,
      });
    },
    {
      onSuccess: (_, variables) => {
        queryClient.refetchQueries({
          queryKey: [QueryKeyFactory.module.wx.settings.settingTask()],
        });
        toast({
          title: "Status Changed",
          description: `${variables.name} is now ${
            variables.isActive ? "active" : "inactive"
          }`,
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to update status",
          variant: "destructive",
        });
      },
    }
  );
};
export const useOperationTypes = (searchQuery?: string) => {
  return useSmartQuery(
    [QueryKeyFactory.module.wx.settings.settingOperationTypes(), searchQuery],
    async () => {
      const response = await workOrderTypesApi.getOperationTypes();
      // Filter out "No" option (key: 0) as it's not a valid operation type
      return response.result.filter((op) => op.key !== 0);
    },
    {
      staleTime: 15 * 60 * 1000, // 15 minutes - operation types don't change often
      refetchOnWindowFocus: false,
    }
  );
};
export const usePPEEquipment = (searchQuery?: string) => {
  const utilityId = useRemoteUtilityId();

  return useSmartQuery(
    [QueryKeyFactory.module.wx.settings.pPEEquipment(), searchQuery],
    async () => {
      const response = await ppeEquipmentApi.getPPEEquipment(
        utilityId,
        searchQuery
      );
      return response.result.map(transformPPEEquipment);
    },
    {
      enabled: !!utilityId,
      staleTime: 5 * 60 * 1000,
    }
  );
};

export const useCreatePPEEquipment = () => {
  const utilityId = useRemoteUtilityId();
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (data: { name: string; description: string; type: string }) => {
      if (!utilityId) throw new Error("Utility ID is missing");
      const payload = convertToSnakeCase({
        remoteUtilityId: utilityId,
        name: data.name,
        description: data.description,
        type: 1,
      });
      return ppeEquipmentApi.createPPEEquipment(payload);
    },
    {
      onSuccess: (response) => {
        queryClient.refetchQueries({
          queryKey: [QueryKeyFactory.module.wx.settings.pPEEquipment()],
        });
        toast({
          title: "Success",
          description: `PPE equipment "${response.result.name}" created successfully`,
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to create PPE equipment",
          variant: "destructive",
        });
      },
    }
  );
};

export const useUpdatePPEEquipment = () => {
  const utilityId = useRemoteUtilityId();
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (data: {
      id: number;
      name: string;
      description: string;
      type: string;
    }) => {
      if (!utilityId) throw new Error("Utility ID is missing");
      const payload = convertToSnakeCase({
        remoteUtilityId: utilityId,
        name: data.name,
        description: data.description,
        type: 1,
      });
      return ppeEquipmentApi.updatePPEEquipment(data.id, payload);
    },
    {
      onSuccess: (response) => {
        queryClient.refetchQueries({
          queryKey: [QueryKeyFactory.module.wx.settings.pPEEquipment()],
        });
        toast({
          title: "Success",
          description: `PPE equipment "${response.result.name}" updated successfully`,
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to update PPE equipment",
          variant: "destructive",
        });
      },
    }
  );
};

export const useTogglePPEEquipmentStatus = () => {
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (data: { id: number; isActive: boolean; name: string }) => {
      const payload = convertToSnakeCase({
        isActive: data.isActive,
      });
      return ppeEquipmentApi.togglePPEEquipmentStatus(data.id, payload);
    },
    {
      onSuccess: (response, variables) => {
        queryClient.refetchQueries({
          queryKey: [QueryKeyFactory.module.wx.settings.pPEEquipment()],
        });
        toast({
          title: "Status Changed",
          description: `${variables.name} is now ${
            variables.isActive ? "isActive" : "inactive"
          }`,
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to update status",
          variant: "destructive",
        });
      },
    }
  );
};
export const useSafetyPackages = (searchQuery?: string) => {
  const utilityId = useRemoteUtilityId();

  // Get lookup data - these hooks should already exist
  const { data: protocolsData } = useSafetyProtocols();
  const { data: equipmentData } = usePPEEquipment();

  return useSmartQuery(
    [
      QueryKeyFactory.module.wx.settings.safetyPackage(),
      searchQuery,
      protocolsData?.length,
      equipmentData?.length,
    ],
    async () => {
      const response = await safetyPackageApi.getSafetyPackages(
        utilityId,
        searchQuery
      );

      // Use enhanced transform with lookup data
      return response.result.map((pkg) =>
        transformSafetyPackageWithLookup(
          pkg,
          protocolsData || [],
          equipmentData || []
        )
      );
    },
    {
      enabled: !!utilityId,
      staleTime: 5 * 60 * 1000,
    }
  );
};

export const useCreateSafetyPackage = () => {
  const utilityId = useRemoteUtilityId();
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (data: {
      name: string;
      description: string;
      selectedProtocols: number[];
      selectedPPE: number[];
    }) => {
      if (!utilityId) throw new Error("Utility ID is missing");

      const payload = convertToSnakeCase({
        remoteUtilityId: utilityId,
        name: data.name,
        description: data.description,
        safetyProtocol: data.selectedProtocols || [],
        safetyEquipment: data.selectedPPE || [],
      });
      return safetyPackageApi.createSafetyPackage(payload);
    },
    {
      onSuccess: (response) => {
        queryClient.refetchQueries({
          queryKey: [QueryKeyFactory.module.wx.settings.safetyPackage()],
        });
        toast({
          title: "Success",
          description: `Safety package "${response.result.name}" created successfully`,
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to create safety package",
          variant: "destructive",
        });
      },
    }
  );
};

export const useUpdateSafetyPackage = () => {
  const utilityId = useRemoteUtilityId();
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (data: {
      id: number;
      name: string;
      description: string;
      selectedProtocols: number[];
      selectedPPE: number[];
    }) => {
      if (!utilityId) throw new Error("Utility ID is missing");
      const payload = convertToSnakeCase({
        remoteUtilityId: utilityId,
        name: data.name,
        description: data.description,
        safetyProtocol: data.selectedProtocols || [],
        safetyEquipment: data.selectedPPE || [],
      });
      return safetyPackageApi.updateSafetyPackage(data.id, payload);
    },
    {
      onSuccess: (response) => {
        queryClient.refetchQueries({
          queryKey: [QueryKeyFactory.module.wx.settings.safetyPackage()],
        });
        toast({
          title: "Success",
          description: `Safety package "${response.result.name}" updated successfully`,
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to update safety package",
          variant: "destructive",
        });
      },
    }
  );
};

export const useToggleSafetyPackageStatus = () => {
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (data: { id: number; isActive: boolean; name: string }) => {
      const payload = convertToSnakeCase({
        isActive: data.isActive,
      });
      return safetyPackageApi.toggleSafetyPackageStatus(data.id, payload);
    },
    {
      onSuccess: (response, variables) => {
        queryClient.refetchQueries({
          queryKey: [QueryKeyFactory.module.wx.settings.safetyPackage()],
        });
        toast({
          title: "Status Changed",
          description: `${variables.name} is now ${
            variables.isActive ? "isActive" : "inactive"
          }`,
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to update status",
          variant: "destructive",
        });
      },
    }
  );
};
export const useSafetyProtocols = (searchQuery?: string) => {
  const utilityId = useRemoteUtilityId();

  return useSmartQuery(
    [QueryKeyFactory.module.wx.settings.safetyProtocol(), searchQuery],
    async () => {
      const response = await safetyProtocolApi.getSafetyProtocols(
        utilityId,
        searchQuery
      );
      return response.result.map(transformSafetyProtocol);
    },
    {
      enabled: !!utilityId,
      staleTime: 5 * 60 * 1000,
    }
  );
};

export const useCreateSafetyProtocol = () => {
  const utilityId = useRemoteUtilityId();
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (data: { name: string; description: string }) => {
      const payload = convertToSnakeCase({
        remoteUtilityId: utilityId,
        name: data.name,
        description: data.description,
        type: 2,
      });
      return safetyProtocolApi.createSafetyProtocol(payload);
    },
    {
      onSuccess: (response) => {
        queryClient.refetchQueries({
          queryKey: [QueryKeyFactory.module.wx.settings.safetyProtocol()],
        });
        toast({
          title: "Success",
          description: `Safety protocol "${response.result.name}" created successfully`,
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to create safety protocol",
          variant: "destructive",
        });
      },
    }
  );
};

export const useUpdateSafetyProtocol = () => {
  const utilityId = useRemoteUtilityId();
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (data: { id: number; name: string; description: string }) => {
      if (!utilityId) throw new Error("Utility ID is missing");

      const payload = convertToSnakeCase({
        remoteUtilityId: utilityId,
        name: data.name,
        description: data.description,
        type: 2,
      });

      return safetyProtocolApi.updateSafetyProtocol(data.id, payload);
    },
    {
      onSuccess: (response) => {
        queryClient.refetchQueries({
          queryKey: [QueryKeyFactory.module.wx.settings.safetyProtocol()],
        });
        toast({
          title: "Success",
          description: `Safety protocol "${response.result.name}" updated successfully`,
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to update safety protocol",
          variant: "destructive",
        });
      },
    }
  );
};

export const useToggleSafetyProtocolStatus = () => {
  const queryClient = useQueryClient();

  return useSmartMutation(
    async (data: { id: number; isActive: boolean; name: string }) => {
      const payload = convertToSnakeCase({
        isActive: data.isActive,
      });
      return safetyProtocolApi.toggleSafetyProtocolStatus(data.id, payload);
    },
    {
      onSuccess: (response, variables) => {
        queryClient.refetchQueries({
          queryKey: [QueryKeyFactory.module.wx.settings.safetyProtocol()],
        });
        toast({
          title: "Status Changed",
          description: `${variables.name} is now ${
            variables.isActive ? "isActive" : "inactive"
          }`,
        });
      },
      onError: (error: any) => {
        toast({
          title: "Error",
          description: error?.message || "Failed to update status",
          variant: "destructive",
        });
      },
    }
  );
};
